// src/contexts/AuthContext.js
import { jwtDecode } from 'jwt-decode';
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useGoogleLogin } from '@react-oauth/google'; // Import the hook
import axios from 'axios'; // For sending token to backend
import { config } from "../config/appConfig";

const AuthContext = createContext({
    isAuthenticated: false,
    user: null,
    token: null,
    login: (token) => { },
    logout: () => { },
    refreshUser: () => { }, // Add refreshUser function
    triggerGoogleLogin: () => { console.warn("Google login trigger not ready."); }, // Add trigger function
    isLoading: true,
    isLoggingIn: false,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoggingIn, setIsLoggingIn] = useState(false);

    const handleLogout = useCallback(() => {
        console.log("AuthContext: Logging out...");
        localStorage.removeItem('authToken');
        localStorage.removeItem('google_avatar');
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
        setIsLoggingIn(false);
    }, []);

    const handleBackendVerify = useCallback(async (idToken) => {
        if (!idToken) { 
            console.error('Google Login did not return a credential.'); 
            setIsLoggingIn(false);
            return; 
        }
        console.log("ID Token received, sending to backend:", idToken.substring(0, 20) + "...");
        try {
            const ref = localStorage.getItem('referralCode');
            // Ensure we use the proper endpoint for Google verification with referral code
            const backendUrl = config.api.auth.googleVerify;

            const response = await axios.post(backendUrl, {
                token: idToken,
                referralCode: ref
            });
            if (response.data && response.data.jwtToken) {
                if (ref) localStorage.removeItem('referralCode'); // Clear after successful use
                login(response.data.jwtToken); // Use internal login function to set state
            } else {
                console.error('Backend did not return a valid JWT.');
                handleLogout(); // Ensure clean state on error
            }
        } catch (error) {
            if (error.response) {
                console.error('Error verifying token with backend - Status:', error.response.status, 'Data:', error.response.data);
            } else if (error.request) { console.error('Error verifying token - No response:', error.request); }
            else { console.error('Error verifying token - Request setup:', error.message); }
            handleLogout(); // Ensure clean state on error
        } finally {
            setIsLoggingIn(false);
        }
    }, [handleLogout]); // login is declared below

    // Use the hook internally
    const googleLogin = useGoogleLogin({
        onSuccess: async (tokenResponse) => {
            console.log("Google Login Success (useGoogleLogin):", tokenResponse);
            if (tokenResponse?.access_token) {
                try {
                    const userInfoRes = await axios.get('https://www.googleapis.com/oauth2/v3/userinfo', {
                        headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
                    });
                    if (userInfoRes.data?.picture) {
                        localStorage.setItem('google_avatar', userInfoRes.data.picture);
                    }
                } catch (err) {
                    console.warn("Could not fetch Google profile picture:", err);
                }
            }
            handleBackendVerify(tokenResponse.access_token);
        },
        onError: (errorResponse) => {
            console.error('Google Login Failed:', errorResponse);
            setIsLoggingIn(false);
        },
        onNonOAuthError: (nonOAuthError) => {
            console.warn('Google Login Non-OAuth Error (e.g. popup closed):', nonOAuthError);
            setIsLoggingIn(false);
        },
    });

    const triggerLogin = useCallback((options) => {
        setIsLoggingIn(true);
        try {
            googleLogin(options);
        } catch (err) {
            console.error("Failed to start Google login:", err);
            setIsLoggingIn(false);
        }
    }, [googleLogin]);

    // Safety timeout: reset isLoggingIn if stuck
    useEffect(() => {
        if (!isLoggingIn) return;
        const timer = setTimeout(() => {
            setIsLoggingIn(false);
        }, 75000);
        return () => clearTimeout(timer);
    }, [isLoggingIn]);

    // Safety check when window regains focus after closing the popup
    useEffect(() => {
        if (!isLoggingIn) return;
        let focusTimer;
        const handleFocus = () => {
            focusTimer = setTimeout(() => {
                setIsLoggingIn((prev) => {
                    if (prev && !localStorage.getItem('authToken')) {
                        return false;
                    }
                    return prev;
                });
            }, 2500);
        };
        window.addEventListener('focus', handleFocus);
        return () => {
            window.removeEventListener('focus', handleFocus);
            if (focusTimer) clearTimeout(focusTimer);
        };
    }, [isLoggingIn]);

    const login = useCallback((newToken) => {
        try {
            // Basic validation: check if it's a string and has three parts
            if (typeof newToken !== 'string' || newToken.split('.').length !== 3) {
                throw new Error("Invalid token format received.");
            }
            localStorage.setItem('authToken', newToken);
            const decoded = jwtDecode(newToken);
            const googleAvatar = localStorage.getItem('google_avatar');
            const userData = {
                id: decoded.sub,
                email: decoded.email,
                plan: decoded.plan,
                avatar: googleAvatar || decoded.picture || decoded.avatar || null
            };
            setToken(newToken);
            setUser(userData);
            setIsAuthenticated(true);
            console.log("AuthContext: User logged in:", userData);
        } catch (error) {
            console.error("AuthContext: Failed to decode or set token:", error);
            handleLogout(); // Clear state if token is invalid
        }
    }, []); // No dependencies needed

    // Function to refresh user data from server and localStorage
    const refreshUser = useCallback(async () => {
        const storedToken = localStorage.getItem('authToken');
        if (storedToken) {
            try {
                const response = await axios.get(config.api.auth.refresh, {
                    headers: { Authorization: `Bearer ${storedToken}` }
                });
                if (response.data && response.data.jwtToken) {
                    login(response.data.jwtToken);
                    return true;
                }
            } catch (err) {
                console.error("AuthContext: Failed to fetch fresh token from server during refresh", err);
            }

            // Fallback to local parsing
            try {
                const decoded = jwtDecode(storedToken);
                if (decoded.exp * 1000 > Date.now()) {
                    const googleAvatar = localStorage.getItem('google_avatar');
                    const userData = {
                        id: decoded.sub,
                        email: decoded.email,
                        plan: decoded.plan,
                        avatar: googleAvatar || decoded.picture || decoded.avatar || null
                    };
                    setUser(userData);
                    setToken(storedToken);
                    setIsAuthenticated(true);
                    console.log("AuthContext: User data refreshed (locally):", userData);
                } else {
                    console.log("AuthContext: Stored token expired during local refresh.");
                    handleLogout();
                }
            } catch (error) {
                console.error("AuthContext: Invalid stored token found during refresh.");
                handleLogout();
            }
        }
        return false;
    }, [login, handleLogout]);


    useEffect(() => {
        console.log("AuthContext: Initializing - checking stored token...");
        const storedToken = localStorage.getItem('authToken');
        if (storedToken) {
            try {
                const decoded = jwtDecode(storedToken);
                if (decoded.exp * 1000 > Date.now()) {
                    console.log("AuthContext: Valid stored token found.");
                    login(storedToken);
                } else {
                    console.log("AuthContext: Stored token expired.");
                    handleLogout();
                }
            } catch (error) {
                console.error("AuthContext: Invalid stored token found.");
                handleLogout();
            }
        } else {
            console.log("AuthContext: No stored token found.");
        }
        setIsLoading(false);
        console.log("AuthContext: Initialization complete.");
    }, []); // Run only once on mount


    return (
        <AuthContext.Provider value={{
            isAuthenticated,
            user,
            token,
            login, // Keep login if needed externally (e.g., manual login form)
            logout: handleLogout, // Expose handleLogout as logout
            refreshUser, // Expose the refreshUser function
            triggerGoogleLogin: triggerLogin, // Expose wrapped trigger function
            isLoading,
            isLoggingIn
        }}>
            {children}
        </AuthContext.Provider>
    );
};